/**
 * Push notifications in the iOS app, at the root of the router: a tapped
 * notification opens its page (usually the trip), and with permission given,
 * each launch and sign-in puts this phone on the signed-in account
 * (src/native/push.js). Renders nothing.
 */
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { isNativeApp } from '@/native/bootstrap';
import { onNotificationTap, syncPushRegistration } from '@/native/push';

export default function NativePushBridge() {
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  useEffect(() => {
    if (!isNativeApp) return undefined;
    let stopped = false;
    let removeTap = () => {};
    let unsubscribe = () => {};

    // A launch from a tap delivers the tap once this listener exists.
    onNotificationTap((path) => navigateRef.current(path)).then((remove) => {
      if (stopped) remove();
      else removeTap = remove;
    });

    let syncedFor = null;
    import('@/lib/supabase').then(({ supabase }) => {
      if (stopped) return;
      const { data } = supabase.auth.onAuthStateChange((event, session) => {
        const userId = session?.user?.id;
        if (!userId || userId === syncedFor) return;
        if (event !== 'INITIAL_SESSION' && event !== 'SIGNED_IN') return;
        syncedFor = userId;
        syncPushRegistration();
      });
      unsubscribe = () => data?.subscription?.unsubscribe();
    }).catch(() => {});

    return () => { stopped = true; removeTap(); unsubscribe(); };
  }, []);

  return null;
}
